<template>
  <div>
    <div class="header">
      <h4>Organizations</h4>
      <div class="buttons">
        <el-button
          type="primary"
          @click="create"
          size="mini"
          icon="el-icon-plus"
          round
          :loading="loading"
          >New</el-button
        >
      </div>
    </div>

    <Table
      :items="organizations"
      :columns="table.columns"
      :actions="table.actions"
      :loading="loading"
    />

    <EditDialog v-if="showEditDialog" :item="item" :isOpen="true" :onCancel="cancel" :onSave="save" />
    <OrgMembers v-if="showOrgMembers" :org="item" :isOpen="true" :onCancel="() => (showOrgMembers = false)" />
  </div>
</template>
<script>
import EditDialog from "./OrganizationsDialog";
import OrgMembers from "./OrgMembers";
import Table from "@/components/Table";
import { cloneDeep, pick } from "lodash";
import { defineComponent, onMounted, computed, ref } from "vue";
import { useStore } from "@/store";
import { useNotify } from "@/utils/notify";

export default defineComponent({
  name: "organizations",
  components: {
    EditDialog,
    Table,
    OrgMembers,
  },
  setup() {
    const { state, dispatch } = useStore();

    const loading = ref(false);
    const showEditDialog = ref(false);
    const showOrgMembers = ref(false);
    const item = ref({});
    const organizations = computed(() => state.enterprise.organizations);

    const { success, error } = useNotify();

    onMounted(() => {
      if (!organizations.length) getOrganizations();
    });

    const getOrganizations = async () => {
      loading.value = true;
      await dispatch("enterprise/getOrganizations");
      loading.value = false;
    };

    const remove = async (item) => {
      try {
        loading.value = true;
        await dispatch("enterprise/deleteOrganization", item.id);
        success({ title: "Organization removed" });
      } catch (err) {
        error({
          title: "Failed in removing organization",
          message: err.message,
        });
      } finally {
        loading.value = false;
      }
    };

    const create = () => {
      item.value = {};
      showEditDialog.value = true;
    };

    const edit = (itemToEdit) => {
      item.value = cloneDeep(itemToEdit);
      showEditDialog.value = true;
    };

    const viewMembers = (itemToView) => {
      showOrgMembers.value = true;
      item.value = itemToView;
    };

    const cancel = () => {
      item.value = {};
      showEditDialog.value = false;
    };

    const save = async () => {
      try {
        loading.value = true;
        const payload = pick(item.value, [
          "name",
          "domain",
          "file",
          "sign_saml_requests",
          "zero_data_persistence"
        ]);

        if (item.value.id) {
          await dispatch("enterprise/updateOrganization", {
            id: item.value.id,
            payload,
          });
        } else {
          await dispatch("enterprise/createOrganization", payload);
        }
        success({ title: "Organization saved" });
        showEditDialog.value = false;
      } catch (err) {
        error({
          title: "Failed in saving organization",
          message: err.message,
        });
      } finally {
        loading.value = false;
      }
    };

    const table = {
      columns: [
        { label: "ID", prop: "id" },
        { label: "Name", prop: "name" },
        { label: "Domain", prop: "domain" },
        {
          label: "SSO Enabled",
          formatter: (item) => (item.idp_metadata_url ? "✅" : "❌"),
        },
        {
          label: "Zero Data Persistence",
          formatter: (item) => (item.zero_data_persistence ? "✅" : "❌"),
        },
      ],
      actions: [
        { name: "Edit", type: "default", onClick: edit },
        { name: "Members", type: "primary", onClick: viewMembers },
        {
          name: "Remove",
          type: "danger",
          onClick: remove,
          requiresConfirm: true,
        },
      ],
    };

    return {
      item,
      showEditDialog,
      showOrgMembers,
      table,
      organizations,
      loading,
      create,
      cancel,
      save,
      getOrganizations,
    };
  },
});
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  align-items: center;
  margin: 10px 0;
}
h4 {
  margin-right: 10px;
}
</style>
